import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Avatar,
  List,
  ListItem,
  ListItemText,
  // ListItemIcon,
  ListItemSecondaryAction,
  Tooltip,
  Alert,
  CircularProgress,
  // Accordion,
  // AccordionSummary,
  // AccordionDetails,
  FormControlLabel,
  Switch,
  Divider,
  Menu,
  ListItemAvatar,
  Container,
  Fade,
  Zoom,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  People,
  Person,
  PersonAdd,
  Settings,
  MoreVert,
  // ExpandMore,
  // Security,
  AdminPanelSettings,
  // Group,
  Visibility,
  Star,
  CheckCircle,
  Cancel,
  Search,
  Groups
} from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';
// import { useAuth } from '../contexts/AuthContext';

interface Team {
  id: number;
  name: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  member_count: number;
  creator?: {
    id: number;
    email: string;
  };
  settings?: {
    is_public?: boolean;
    auto_approve_members?: boolean;
    allow_member_invites?: boolean;
  };
}

interface TeamMember {
  id: number;
  user_id: number;
  team_id: number;
  role: 'member' | 'admin' | 'owner';
  joined_at: string;
  is_active: boolean;
  user: {
    id: number;
    email: string;
    role: string;
    is_active: boolean;
  };
}

interface User {
  id: number;
  email: string;
  role: string;
  is_active: boolean;
  created_at: string;
}

const TeamManagement: React.FC = () => {
  // const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [teamSearchQuery, setTeamSearchQuery] = useState('');

  const [newTeam, setNewTeam] = useState({
    name: '',
    description: '',
    is_public: false,
    auto_approve_members: true,
    allow_member_invites: false
  });

  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [memberRole, setMemberRole] = useState<'member' | 'admin'>('member');

  useEffect(() => {
    fetchTeams();
    fetchAvailableUsers();
  }, []);

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/teams');
      setTeams(response.data.data || []);
    } catch (error: any) {
      console.error('Failed to fetch teams:', error);
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamMembers = async (teamId: number) => {
    try {
      const response = await axios.get(`/api/teams/${teamId}`);
      setTeamMembers(response.data.data.members || []);
    } catch (error: any) {
      console.error('Failed to fetch team members:', error);
      toast.error('Failed to load team members');
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const response = await axios.get('/api/organizations/me/members');
      // Transform organization members to match User interface
      const users = (response.data.members || []).map((member: any) => ({
        id: member.User?.id || member.user_id,
        email: member.User?.email || 'Unknown',
        role: member.User?.role || 'user',
        is_active: member.User?.is_active ?? true,
        created_at: member.User?.created_at || new Date().toISOString()
      }));
      setAvailableUsers(users);
    } catch (error: any) {
      console.error('Failed to fetch available users:', error);
    }
  };

  const createTeam = async () => {
    try {
      console.log('Creating team with data:', newTeam);
      const response = await axios.post('/api/teams', newTeam);

      console.log('Team creation response:', response.data);
      toast.success('Team created successfully!');
      setCreateDialogOpen(false);
      setNewTeam({
        name: '',
        description: '',
        is_public: false,
        auto_approve_members: true,
        allow_member_invites: false
      });

      fetchTeams();
    } catch (error: any) {
      console.error('Failed to create team:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);

      const errorMessage =
        error.response?.data?.message || error.response?.data?.error || 'Failed to create team';

      // Show more specific error messages
      if (error.response?.status === 403) {
        if (error.response.data?.code === 'ACCESS_DENIED') {
          toast.error(
            'Access denied. You need admin, department head, or team lead role to create teams.'
          );
        } else if (error.response.data?.code === 'TEAM_LIMIT_REACHED') {
          toast.error('Organization has reached maximum team limit.');
        } else {
          toast.error(errorMessage);
        }
      } else if (error.response?.status === 404) {
        toast.error('You are not part of any organization. Please contact your administrator.');
      } else if (error.response?.status === 400) {
        const validationErrors = error.response.data?.details;
        if (validationErrors && Array.isArray(validationErrors)) {
          validationErrors.forEach((err: any) => {
            toast.error(err.msg || err.message);
          });
        } else {
          toast.error(errorMessage);
        }
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const updateTeam = async () => {
    if (!selectedTeam) return;

    try {
      await axios.put(`/api/teams/${selectedTeam.id}`, {
        name: selectedTeam.name,
        description: selectedTeam.description,
        is_active: selectedTeam.is_active
      });

      toast.success('Team updated successfully!');
      setEditDialogOpen(false);
      fetchTeams();
    } catch (error: any) {
      console.error('Failed to update team:', error);
      toast.error(error.response?.data?.message || 'Failed to update team');
    }
  };

  const deleteTeam = async (teamId: number) => {
    if (
      !window.confirm('Are you sure you want to delete this team? This action cannot be undone.')
    ) {
      return;
    }

    try {
      await axios.delete(`/api/teams/${teamId}`);
      toast.success('Team deleted successfully');
      fetchTeams();
    } catch (error: any) {
      console.error('Failed to delete team:', error);
      toast.error(error.response?.data?.message || 'Failed to delete team');
    }
  };

  const addMembersToTeam = async () => {
    if (!selectedTeam || selectedUsers.length === 0) return;

    try {
      const response = await axios.post(`/api/teams/${selectedTeam.id}/members`, {
        user_ids: selectedUsers,
        role: memberRole
      });

      const { data } = response.data;
      const successful = data.successful || [];
      const failed = data.failed || [];

      if (successful.length > 0) {
        toast.success(`Successfully added ${successful.length} member(s) to team`);
      }

      if (failed.length > 0) {
        const failedMessages = failed.map((f: any) => `User ${f.user_id}: ${f.error}`).join(', ');
        toast.error(`Failed to add ${failed.length} member(s): ${failedMessages}`);
      }

      setAddMemberDialogOpen(false);
      setSelectedUsers([]);
      setMemberRole('member');
      fetchTeamMembers(selectedTeam.id);
    } catch (error: any) {
      console.error('Failed to add members:', error);
      toast.error(error.response?.data?.message || 'Failed to add members');
    }
  };

  const updateMemberRole = async (userId: number, newRole: string) => {
    if (!selectedTeam) return;

    try {
      await axios.put(`/api/teams/${selectedTeam.id}/members/${userId}/role`, {
        role: newRole
      });

      toast.success('Member role updated successfully');
      fetchTeamMembers(selectedTeam.id);
    } catch (error: any) {
      console.error('Failed to update member role:', error);
      toast.error(error.response?.data?.message || 'Failed to update member role');
    }
  };

  const removeMember = async (userId: number) => {
    if (!selectedTeam) return;

    if (!window.confirm('Are you sure you want to remove this member from the team?')) {
      return;
    }

    try {
      await axios.delete(`/api/teams/${selectedTeam.id}/members/${userId}`);
      toast.success('Member removed from team');
      fetchTeamMembers(selectedTeam.id);
    } catch (error: any) {
      console.error('Failed to remove member:', error);
      toast.error(error.response?.data?.message || 'Failed to remove member');
    }
  };

  const updateTeamSettings = async () => {
    if (!selectedTeam) return;

    try {
      await axios.put(`/api/teams/${selectedTeam.id}/settings`, selectedTeam.settings);
      toast.success('Team settings updated successfully');
      setSettingsDialogOpen(false);
      fetchTeams();
    } catch (error: any) {
      console.error('Failed to update team settings:', error);
      toast.error(error.response?.data?.message || 'Failed to update settings');
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'error';
      case 'admin':
        return 'warning';
      case 'member':
        return 'info';
      default:
        return 'default';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner':
        return <Star />;
      case 'admin':
        return <AdminPanelSettings />;
      case 'member':
        return <Person />;
      default:
        return <Person />;
    }
  };

  const filteredUsers = availableUsers.filter((u) => {
    const matchesSearch =
      searchQuery === '' ||
      u.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.role.toLowerCase().includes(searchQuery.toLowerCase());

    const notAlreadyMember = !teamMembers.some(
      (member) => member.user?.id === u.id || member.user_id === u.id
    );

    return matchesSearch && notAlreadyMember;
  });

  const filteredTeamMembers = teamMembers.filter((member) => {
    if (memberSearchQuery === '') return true;

    const email = member.user?.email || '';
    const role = member.role || '';
    const userId = member.user?.id || member.user_id || '';

    return (
      email.toLowerCase().includes(memberSearchQuery.toLowerCase()) ||
      role.toLowerCase().includes(memberSearchQuery.toLowerCase()) ||
      userId.toString().includes(memberSearchQuery)
    );
  });

  const filteredTeams = teams.filter((team) => {
    if (teamSearchQuery === '') return true;

    const name = team.name || '';
    const description = team.description || '';

    return (
      name.toLowerCase().includes(teamSearchQuery.toLowerCase()) ||
      description.toLowerCase().includes(teamSearchQuery.toLowerCase())
    );
  });

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Enhanced Header with Gradient Background */}
        <Fade in timeout={600}>
          <Paper
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              p: 4,
              mb: 4,
              borderRadius: 3,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)',
                pointerEvents: 'none'
              }
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  <Groups sx={{ fontSize: 36 }} />
                </Avatar>
                <Box flex={1}>
                  <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold" gutterBottom>
                    Team Management
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.95 }}>
                    Create and manage teams within your organization. Organize users into teams for
                    better collaboration and access control.
                  </Typography>
                </Box>
                {!isMobile && (
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<Add />}
                    onClick={() => setCreateDialogOpen(true)}
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.3)'
                      }
                    }}
                  >
                    Create Team
                  </Button>
                )}
              </Box>
              {isMobile && (
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  startIcon={<Add />}
                  onClick={() => setCreateDialogOpen(true)}
                  sx={{
                    mt: 2,
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.3)'
                    }
                  }}
                >
                  Create Team
                </Button>
              )}
            </Box>
          </Paper>
        </Fade>

        {/* Statistics Dashboard */}
        {teams.length > 0 && (
          <Fade in timeout={800}>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Zoom in timeout={600}>
                  <Paper
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      background:
                        'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                      border: '1px solid',
                      borderColor: 'rgba(102, 126, 234, 0.2)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar
                        sx={{
                          bgcolor: 'primary.main',
                          width: 56,
                          height: 56
                        }}
                      >
                        <Groups sx={{ fontSize: 28 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="h4" fontWeight="bold">
                          {teams.length}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Total Teams
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Zoom>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Zoom in timeout={700}>
                  <Paper
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      background:
                        'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(56, 142, 60, 0.1) 100%)',
                      border: '1px solid',
                      borderColor: 'rgba(76, 175, 80, 0.2)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar
                        sx={{
                          bgcolor: 'success.main',
                          width: 56,
                          height: 56
                        }}
                      >
                        <CheckCircle sx={{ fontSize: 28 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="h4" fontWeight="bold">
                          {teams.filter((t) => t.is_active).length}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Active Teams
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Zoom>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Zoom in timeout={800}>
                  <Paper
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      background:
                        'linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(25, 118, 210, 0.1) 100%)',
                      border: '1px solid',
                      borderColor: 'rgba(33, 150, 243, 0.2)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar
                        sx={{
                          bgcolor: 'info.main',
                          width: 56,
                          height: 56
                        }}
                      >
                        <People sx={{ fontSize: 28 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="h4" fontWeight="bold">
                          {teams.reduce((sum, team) => sum + (team.member_count || 0), 0)}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Total Members
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Zoom>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Zoom in timeout={900}>
                  <Paper
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      background:
                        'linear-gradient(135deg, rgba(255, 152, 0, 0.1) 0%, rgba(245, 124, 0, 0.1) 100%)',
                      border: '1px solid',
                      borderColor: 'rgba(255, 152, 0, 0.2)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar
                        sx={{
                          bgcolor: 'warning.main',
                          width: 56,
                          height: 56
                        }}
                      >
                        <Star sx={{ fontSize: 28 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="h4" fontWeight="bold">
                          {teams.length > 0
                            ? Math.round(
                                teams.reduce((sum, team) => sum + (team.member_count || 0), 0) /
                                  teams.length
                              )
                            : 0}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Avg Members/Team
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Zoom>
              </Grid>
            </Grid>
          </Fade>
        )}

        {/* Team Search */}
        {teams.length > 0 && (
          <Fade in timeout={1000}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 3,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper'
              }}
            >
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Avatar
                  sx={{
                    bgcolor: 'primary.light',
                    width: 40,
                    height: 40
                  }}
                >
                  <Search />
                </Avatar>
                <Typography variant="h6" fontWeight="bold">
                  Search & Filter
                </Typography>
              </Box>
              <TextField
                fullWidth
                label="Search Teams"
                placeholder="Search by team name or description..."
                value={teamSearchQuery}
                onChange={(e) => setTeamSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                helperText={teamSearchQuery && `Found ${filteredTeams.length} team(s)`}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'background.default',
                    '&:hover': {
                      bgcolor: 'background.paper'
                    }
                  }
                }}
              />
            </Paper>
          </Fade>
        )}

        {/* Teams Grid */}
        <Grid container spacing={3}>
          {teams.length === 0 ? (
            <Grid item xs={12}>
              <Fade in timeout={1000}>
                <Paper
                  sx={{
                    p: 6,
                    textAlign: 'center',
                    borderRadius: 3,
                    border: '2px dashed',
                    borderColor: 'divider',
                    bgcolor: 'background.paper'
                  }}
                >
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: 'primary.light',
                      color: 'primary.main',
                      mx: 'auto',
                      mb: 2
                    }}
                  >
                    <Groups sx={{ fontSize: 48 }} />
                  </Avatar>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    No Teams Created Yet
                  </Typography>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Create your first team to start organizing your organization members.
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<Add />}
                    onClick={() => setCreateDialogOpen(true)}
                    sx={{ mt: 2 }}
                  >
                    Create First Team
                  </Button>
                </Paper>
              </Fade>
            </Grid>
          ) : filteredTeams.length === 0 ? (
            <Grid item xs={12}>
              <Fade in timeout={1000}>
                <Paper
                  sx={{
                    p: 6,
                    textAlign: 'center',
                    borderRadius: 3,
                    bgcolor: 'background.paper'
                  }}
                >
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: 'grey.100',
                      color: 'text.secondary',
                      mx: 'auto',
                      mb: 2
                    }}
                  >
                    <Search sx={{ fontSize: 48 }} />
                  </Avatar>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    No Teams Found
                  </Typography>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Try adjusting your search criteria.
                  </Typography>
                  <Button variant="outlined" onClick={() => setTeamSearchQuery('')}>
                    Clear Search
                  </Button>
                </Paper>
              </Fade>
            </Grid>
          ) : (
            filteredTeams.map((team, index) => (
              <Grid item xs={12} sm={6} md={4} key={team.id}>
                <Zoom in timeout={600 + index * 100}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 6
                      }
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        mb={2}
                      >
                        <Box flex={1}>
                          <Typography variant="h6" gutterBottom fontWeight="bold">
                            {team.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            gutterBottom
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              minHeight: '40px'
                            }}
                          >
                            {team.description || 'No description'}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            setMenuAnchor(e.currentTarget);
                            setSelectedTeam(team);
                          }}
                          sx={{
                            bgcolor: 'action.hover',
                            '&:hover': {
                              bgcolor: 'action.selected'
                            }
                          }}
                        >
                          <MoreVert />
                        </IconButton>
                      </Box>

                      <Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
                        <Chip
                          icon={<People />}
                          label={`${team.member_count} member${team.member_count !== 1 ? 's' : ''}`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={team.is_active ? 'Active' : 'Inactive'}
                          color={team.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </Box>

                      <Box display="flex" gap={1} flexDirection={isMobile ? 'column' : 'row'}>
                        <Button
                          size="small"
                          startIcon={<Visibility />}
                          onClick={() => {
                            setSelectedTeam(team);
                            fetchTeamMembers(team.id);
                            setMembersDialogOpen(true);
                          }}
                          fullWidth={isMobile}
                        >
                          View
                        </Button>
                        <Button
                          size="small"
                          startIcon={<PersonAdd />}
                          onClick={() => {
                            setSelectedTeam(team);
                            fetchAvailableUsers();
                            setAddMemberDialogOpen(true);
                          }}
                          fullWidth={isMobile}
                        >
                          Add
                        </Button>
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      <Typography variant="caption" color="textSecondary">
                        Created: {new Date(team.created_at).toLocaleDateString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Zoom>
              </Grid>
            ))
          )}
        </Grid>

        {/* Team Actions Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          PaperProps={{
            sx: {
              borderRadius: 2,
              minWidth: 200
            }
          }}
        >
          <MenuItem
            onClick={() => {
              setEditDialogOpen(true);
              setMenuAnchor(null);
            }}
          >
            <Edit sx={{ mr: 1, fontSize: 20 }} />
            Edit Team
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (selectedTeam) {
                fetchTeamMembers(selectedTeam.id);
                setMembersDialogOpen(true);
              }
              setMenuAnchor(null);
            }}
          >
            <People sx={{ mr: 1, fontSize: 20 }} />
            Manage Members
          </MenuItem>
          <MenuItem
            onClick={() => {
              setSettingsDialogOpen(true);
              setMenuAnchor(null);
            }}
          >
            <Settings sx={{ mr: 1, fontSize: 20 }} />
            Team Settings
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={() => {
              if (selectedTeam) {
                deleteTeam(selectedTeam.id);
              }
              setMenuAnchor(null);
            }}
            sx={{ color: 'error.main' }}
          >
            <Delete sx={{ mr: 1, fontSize: 20 }} />
            Delete Team
          </MenuItem>
        </Menu>

        {/* Create Team Dialog */}
        <Dialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3
            }
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                <Add />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  Create New Team
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Set up a new team for your organization
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Team Name"
                  value={newTeam.name}
                  onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                  required
                  placeholder="Enter team name..."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={newTeam.description}
                  onChange={(e) => setNewTeam({ ...newTeam, description: e.target.value })}
                  placeholder="Describe the team's purpose..."
                />
              </Grid>
              <Grid item xs={12}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'background.default'
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                    Team Settings
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={newTeam.is_public}
                          onChange={(e) => setNewTeam({ ...newTeam, is_public: e.target.checked })}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">Public Team</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Visible to all organization members
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={newTeam.auto_approve_members}
                          onChange={(e) =>
                            setNewTeam({ ...newTeam, auto_approve_members: e.target.checked })
                          }
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">Auto-approve new members</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Automatically approve join requests
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={newTeam.allow_member_invites}
                          onChange={(e) =>
                            setNewTeam({ ...newTeam, allow_member_invites: e.target.checked })
                          }
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">Allow members to invite others</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Team members can send invitations
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={() => setCreateDialogOpen(false)} size="large">
              Cancel
            </Button>
            <Button
              onClick={createTeam}
              variant="contained"
              disabled={!newTeam.name}
              size="large"
              startIcon={<Add />}
            >
              Create Team
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Team Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3
            }
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: 'warning.main' }}>
                <Edit />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  Edit Team
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Update team information
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 3 }}>
            {selectedTeam && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Team Name"
                    value={selectedTeam.name}
                    onChange={(e) => setSelectedTeam({ ...selectedTeam, name: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={3}
                    value={selectedTeam.description || ''}
                    onChange={(e) =>
                      setSelectedTeam({ ...selectedTeam, description: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: 'background.default'
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={selectedTeam.is_active}
                          onChange={(e) =>
                            setSelectedTeam({ ...selectedTeam, is_active: e.target.checked })
                          }
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">Team Active</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {selectedTeam.is_active
                              ? 'Team is currently active'
                              : 'Team is currently inactive'}
                          </Typography>
                        </Box>
                      }
                    />
                  </Paper>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <Divider />
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={() => setEditDialogOpen(false)} size="large">
              Cancel
            </Button>
            <Button
              onClick={updateTeam}
              variant="contained"
              size="large"
              startIcon={<CheckCircle />}
            >
              Update Team
            </Button>
          </DialogActions>
        </Dialog>

        {/* Team Members Dialog */}
        <Dialog
          open={membersDialogOpen}
          onClose={() => {
            setMembersDialogOpen(false);
            setMemberSearchQuery('');
          }}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3
            }
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: 'info.main' }}>
                <People />
              </Avatar>
              <Box flex={1}>
                <Typography variant="h6" fontWeight="bold">
                  Team Members
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {selectedTeam?.name}
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 3 }}>
            {/* Search Bar */}
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Search Members"
                placeholder="Search by email, role, or user ID..."
                value={memberSearchQuery}
                onChange={(e) => setMemberSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                helperText={memberSearchQuery && `Found ${filteredTeamMembers.length} member(s)`}
              />
            </Box>

            {teamMembers.length === 0 ? (
              <Box textAlign="center" py={6}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'grey.100',
                    color: 'text.secondary',
                    mx: 'auto',
                    mb: 2
                  }}
                >
                  <People sx={{ fontSize: 48 }} />
                </Avatar>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  No Members Yet
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Add members to this team to get started.
                </Typography>
              </Box>
            ) : filteredTeamMembers.length === 0 ? (
              <Box textAlign="center" py={6}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'grey.100',
                    color: 'text.secondary',
                    mx: 'auto',
                    mb: 2
                  }}
                >
                  <Search sx={{ fontSize: 48 }} />
                </Avatar>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  No Members Found
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Try adjusting your search criteria.
                </Typography>
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden'
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        sx={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          py: 2
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar
                            sx={{
                              bgcolor: 'rgba(255, 255, 255, 0.2)',
                              backdropFilter: 'blur(10px)',
                              width: 40,
                              height: 40
                            }}
                          >
                            <People />
                          </Avatar>
                          <Box>
                            <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                              Team Members
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: 'rgba(255, 255, 255, 0.9)' }}
                            >
                              Total: {filteredTeamMembers.length} member
                              {filteredTeamMembers.length !== 1 ? 's' : ''}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow
                      sx={{
                        bgcolor: 'action.hover'
                      }}
                    >
                      <TableCell sx={{ fontWeight: 'bold' }}>Member</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Role</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Joined</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTeamMembers.map((member) => (
                      <TableRow
                        key={member.id}
                        sx={{
                          '&:hover': {
                            bgcolor: 'action.hover',
                            borderLeft: '4px solid',
                            borderLeftColor: 'primary.main'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Avatar sx={{ bgcolor: 'primary.light' }}>
                              <Person />
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {member.user?.email || 'Unknown User'}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                User ID: {member.user?.id || member.user_id}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getRoleIcon(member.role)}
                            label={member.role.toUpperCase()}
                            color={getRoleColor(member.role) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(member.joined_at).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={member.is_active ? 'Active' : 'Inactive'}
                            color={member.is_active ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            <FormControl size="small" sx={{ minWidth: 100 }}>
                              <Select
                                value={member.role}
                                onChange={(e) =>
                                  updateMemberRole(
                                    member.user?.id || member.user_id,
                                    e.target.value
                                  )
                                }
                                disabled={member.role === 'owner'}
                              >
                                <MenuItem value="member">Member</MenuItem>
                                <MenuItem value="admin">Admin</MenuItem>
                                {member.role === 'owner' && (
                                  <MenuItem value="owner">Owner</MenuItem>
                                )}
                              </Select>
                            </FormControl>
                            <Tooltip title="Remove member">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => removeMember(member.user?.id || member.user_id)}
                                disabled={member.role === 'owner'}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <Divider />
          <DialogActions sx={{ p: 2.5 }}>
            <Button
              startIcon={<PersonAdd />}
              onClick={() => setAddMemberDialogOpen(true)}
              variant="outlined"
              size="large"
            >
              Add Members
            </Button>
            <Button onClick={() => setMembersDialogOpen(false)} size="large">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Members Dialog */}
        <Dialog
          open={addMemberDialogOpen}
          onClose={() => setAddMemberDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3
            }
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: 'success.main' }}>
                <PersonAdd />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  Add Members
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {selectedTeam?.name}
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Search Users"
                  placeholder="Search by email or role..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                  helperText={searchQuery && `Found ${filteredUsers.length} user(s)`}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Default Role</InputLabel>
                  <Select
                    value={memberRole}
                    onChange={(e) => setMemberRole(e.target.value as 'member' | 'admin')}
                  >
                    <MenuItem value="member">Member</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Available Users ({filteredUsers.length})
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    maxHeight: 300,
                    overflow: 'auto',
                    borderRadius: 2
                  }}
                >
                  {filteredUsers.length === 0 ? (
                    <Box p={4} textAlign="center">
                      <Avatar
                        sx={{
                          width: 60,
                          height: 60,
                          bgcolor: 'grey.100',
                          color: 'text.secondary',
                          mx: 'auto',
                          mb: 2
                        }}
                      >
                        <Person sx={{ fontSize: 32 }} />
                      </Avatar>
                      <Typography variant="body2" color="textSecondary">
                        No available users found
                      </Typography>
                    </Box>
                  ) : (
                    <List>
                      {filteredUsers.map((user) => (
                        <ListItem
                          key={user.id}
                          button
                          onClick={() => {
                            if (selectedUsers.includes(user.id)) {
                              setSelectedUsers(selectedUsers.filter((id) => id !== user.id));
                            } else {
                              setSelectedUsers([...selectedUsers, user.id]);
                            }
                          }}
                          sx={{
                            '&:hover': {
                              bgcolor: 'action.hover'
                            }
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: 'primary.light' }}>
                              <Person />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="body2" fontWeight="medium">
                                {user.email}
                              </Typography>
                            }
                            secondary={`Role: ${user.role} • Joined: ${new Date(user.created_at).toLocaleDateString()}`}
                          />
                          <ListItemSecondaryAction>
                            {selectedUsers.includes(user.id) ? (
                              <CheckCircle color="primary" />
                            ) : (
                              <Cancel color="disabled" />
                            )}
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Paper>
                {selectedUsers.length > 0 && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {selectedUsers.length} user(s) selected to add as {memberRole}(s)
                  </Alert>
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={() => setAddMemberDialogOpen(false)} size="large">
              Cancel
            </Button>
            <Button
              onClick={addMembersToTeam}
              variant="contained"
              disabled={selectedUsers.length === 0}
              size="large"
              startIcon={<PersonAdd />}
            >
              Add {selectedUsers.length} Member(s)
            </Button>
          </DialogActions>
        </Dialog>

        {/* Team Settings Dialog */}
        <Dialog
          open={settingsDialogOpen}
          onClose={() => setSettingsDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3
            }
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: 'secondary.main' }}>
                <Settings />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  Team Settings
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {selectedTeam?.name}
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 3 }}>
            {selectedTeam && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: 'background.default'
                    }}
                  >
                    <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ mb: 2 }}>
                      Team Visibility & Access
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={selectedTeam.settings?.is_public || false}
                          onChange={(e) =>
                            setSelectedTeam({
                              ...selectedTeam,
                              settings: {
                                auto_approve_members: false,
                                allow_member_invites: false,
                                ...selectedTeam.settings,
                                is_public: e.target.checked
                              }
                            })
                          }
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">Public Team</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Public teams are visible to all organization members
                          </Typography>
                        </Box>
                      }
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: 'background.default'
                    }}
                  >
                    <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ mb: 2 }}>
                      Member Management
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={selectedTeam.settings?.auto_approve_members || false}
                            onChange={(e) =>
                              setSelectedTeam({
                                ...selectedTeam,
                                settings: {
                                  is_public: false,
                                  allow_member_invites: false,
                                  ...selectedTeam.settings,
                                  auto_approve_members: e.target.checked
                                }
                              })
                            }
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">Auto-approve Members</Typography>
                            <Typography variant="caption" color="textSecondary">
                              Automatically approve join requests
                            </Typography>
                          </Box>
                        }
                      />

                      <FormControlLabel
                        control={
                          <Switch
                            checked={selectedTeam.settings?.allow_member_invites || false}
                            onChange={(e) =>
                              setSelectedTeam({
                                ...selectedTeam,
                                settings: {
                                  is_public: false,
                                  auto_approve_members: false,
                                  ...selectedTeam.settings,
                                  allow_member_invites: e.target.checked
                                }
                              })
                            }
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">Allow Member Invites</Typography>
                            <Typography variant="caption" color="textSecondary">
                              Allow team members to invite others
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <Divider />
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={() => setSettingsDialogOpen(false)} size="large">
              Cancel
            </Button>
            <Button
              onClick={updateTeamSettings}
              variant="contained"
              size="large"
              startIcon={<CheckCircle />}
            >
              Save Settings
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default TeamManagement;
